import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { format, isValid } from 'date-fns';
import { CheckIcon } from '@heroicons/react/outline';

import { useClassNames } from '../../../hooks/useClassNames';
import { useFeature } from '../../../hooks/useFeature';
import * as Constants from '../../../utils/constants';

import * as InteractApi from '../../../api/interact';
import * as TemplateEnvApi from '../../../api/interactTemplateEnvironment.js';
import * as FileProcessApi from '../../../api/interactTemplateFileProcess.js';

import { TwPage } from '../../../components/TailwindPage';
import { Panel as CmpPanel } from '../../../components/Panel';
import { Button as CmpButton } from '../../../components/Button';
import { FileUpload } from '../../../components/FileUpload';

import { connectionsFileName } from './connectionsHeaderButtons';
import { ReferenceTable } from './importConnectionsReport';
import { summarise, groupByLogicFile } from './importConnectionsModel';
import { SummaryCards, SourceStrip, CleanBill, LogicFileGroup, UnconnectedPanel } from './importConnectionsReview';

const steps = ['Choose export', 'Review changes', 'Done'];
const GROUP_PAGE_SIZE = 50;

// The API accepts 32 MB per request; an export is indented, so the file is larger than what is sent.
const maxExportFileMb = 25;

const readText = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(reader.error);
  reader.readAsText(file);
});

// A body the API cannot bind comes back as ProblemDetails, which has no message, only errors per field.
const firstValidationError = (data) => Object.values(data?.errors || {}).flat().find((e) => typeof e === 'string') || null;

const serverMessage = (err) => err?.response?.data?.Message || err?.response?.data?.message || firstValidationError(err?.response?.data);

// Server messages do not always end in a full stop, and these are joined into longer sentences.
const sentence = (text) => (/[.!?]$/.test(text) ? text : `${text}.`);

const errorText = (err, fallback) => {
  const status = err?.response?.status;
  if (status === 403)
    return 'You do not have access to import connections.';
  if (status === 404)
    return 'This function is not available on the server yet.';
  return sentence(serverMessage(err) || fallback);
};

export default function ImportConnections() {
  const { groupId, environmentId } = useParams();
  const navigate = useNavigate();
  const { hasFeature } = useFeature();
  const { classNames } = useClassNames();

  const [pageState, setPageState] = useState('loading');
  const [target, setTarget] = useState(null);
  const [step, setStep] = useState(0);
  const [document, setDocument] = useState(null);
  const [fileError, setFileError] = useState(null);
  const [checking, setChecking] = useState(false);
  const [plan, setPlan] = useState(null);
  const [filter, setFilter] = useState(null);
  const [query, setQuery] = useState('');
  const [openGroups, setOpenGroups] = useState({});
  const [groupLimit, setGroupLimit] = useState(GROUP_PAGE_SIZE);
  const [acknowledged, setAcknowledged] = useState(false);
  // On is what an import always did: a document the export lists without a connection loses the target's connection.
  const [removeUnconnected, setRemoveUnconnected] = useState(true);
  const [saving, setSaving] = useState(false);
  const [applyError, setApplyError] = useState(null);
  const [planChanged, setPlanChanged] = useState(false);
  const [result, setResult] = useState(null);

  const headingRef = useRef(null);
  const firstRender = useRef(true);
  const mounted = useRef(true);
  const checkingRef = useRef(false);
  // The first backup is the one worth keeping after a failed import, so retries do not take another.
  const backupTaken = useRef(false);

  useEffect(() => () => { mounted.current = false; }, []);

  // Each step replaces the content that held focus; without this, focus falls to the page body.
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    headingRef.current?.focus();
  }, [step]);

  const load = async () => {
    setPageState('loading');
    try {
      const [group, templates, environments] = await Promise.all([
        InteractApi.getEnvironmentGroup(groupId),
        TemplateEnvApi.getInteractTemplateEnvironments(groupId),
        InteractApi.getInteractEnvironments(),
      ]);
      const template = templates?.find(t => t.id === environmentId);
      if (!template) {
        setPageState('missing');
        return;
      }
      const name = environments?.find(e => e.value === template.customerEnvironment)?.name || 'this environment';
      const canImport = template.order === 1 && !group?.isVirtual && hasFeature(Constants.templatesConfigurationFeatureCode);
      setTarget({ template, name, canImport });
      setPageState('loaded');
    } catch {
      setPageState('error');
    }
  };

  useEffect(() => {
    load();
  }, [groupId, environmentId]);

  const check = async (doc) => {
    if (checkingRef.current)
      return;
    checkingRef.current = true;
    setChecking(true);
    setFileError(null);
    setPlanChanged(false);
    setApplyError(null);
    try {
      const preview = await FileProcessApi.previewConnectionImport(environmentId, doc);
      setPlan(preview);
      setFilter(null);
      setQuery('');
      setOpenGroups({});
      setGroupLimit(GROUP_PAGE_SIZE);
      setAcknowledged(false);
      setRemoveUnconnected(true);
      setStep(1);
    } catch (err) {
      setFileError(`${errorText(err, `The file could not be checked against ${target.name}.`)} Choose a file made with Export connections.`);
      setStep(0);
    } finally {
      checkingRef.current = false;
      setChecking(false);
    }
  };

  const onFileChosen = async (file) => {
    if (checkingRef.current)
      return;
    setFileError(null);
    let doc;
    try {
      doc = JSON.parse(await readText(file));
    } catch {
      setFileError('This file is not valid JSON. Choose a file made with Export connections.');
      return;
    }
    setDocument(doc);
    backupTaken.current = false;
    await check(doc);
  };

  const apply = async () => {
    setSaving(true);
    setApplyError(null);
    setPlanChanged(false);
    if (!backupTaken.current) {
      try {
        await FileProcessApi.exportConnections(environmentId, connectionsFileName(target.name, '-backup'));
        backupTaken.current = true;
      } catch (err) {
        if (!mounted.current) return;
        setApplyError(`${errorText(err, 'The backup of the current connections could not be downloaded.')} Nothing was changed. Try again.`);
        setSaving(false);
        return;
      }
    }
    try {
      const imported = await FileProcessApi.importConnections(environmentId, document, plan.planHash, removeUnconnected);
      if (!mounted.current) return;
      setResult(imported);
      setStep(2);
    } catch (err) {
      if (!mounted.current) return;
      if (err?.response?.status === 409) {
        // Nothing was written and the target moved, so the next backup should show the new state.
        backupTaken.current = false;
        setPlanChanged(true);
      } else {
        setApplyError(`The import failed. ${errorText(err, 'Check the connections before you try again.')} Keep the backup file that was downloaded first.`);
      }
    } finally {
      if (mounted.current)
        setSaving(false);
    }
  };

  const backToFiles = () => navigate(`/templates/publish/${groupId}/${target?.template?.order ?? 1}`);

  if (pageState !== 'loaded') {
    return (
      <div data-state={pageState}>
        <TwPage>
          {pageState === 'loading' && (
            <div className="space-y-4" role="status" aria-busy="true" aria-label="Loading">
              <span className="skeleton-loader w-1/3" />
              <span className="skeleton-loader w-2/3" />
            </div>
          )}
          {pageState === 'error' && (
            <div role="alert" className="rounded-md bg-red-50 p-4 dark:bg-red-900">
              <p className="text-sm text-red-800 dark:text-red-100">The environment could not be loaded.</p>
              <div className="mt-3">
                <CmpButton id="page-retry" variant={CmpButton.variants.secondary} onClick={load}>Retry</CmpButton>
              </div>
            </div>
          )}
          {pageState === 'missing' && (
            <div role="alert" className="rounded-md bg-yellow-50 p-4 text-sm text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100">
              This environment does not exist in this group. Go back to the logic files and choose it again.
            </div>
          )}
        </TwPage>
      </div>
    );
  }

  const summary = plan ? summarise(plan, { removeUnconnected }) : null;
  const missingCount = summary?.skipped ?? 0;
  const writeCount = plan ? plan.createCount + plan.updateCount : 0;
  const searching = query.trim() !== '';
  const groups = plan ? groupByLogicFile(plan, { filter, query, removeUnconnected }) : [];
  // Files with something to write, skip or remove start open; a search opens everything it found.
  const isOpen = (g) => openGroups[g.fileName] ?? (g.defaultOpen || searching);
  const allOpen = groups.length > 0 && groups.every(isOpen);
  const setAllOpen = (open) => setOpenGroups(Object.fromEntries(groups.map(g => [g.fileName, open])));
  const toggleCard = (id) => { setFilter(f => (f === id ? null : id)); setGroupLimit(GROUP_PAGE_SIZE); };
  const changeQuery = (value) => { setQuery(value); setGroupLimit(GROUP_PAGE_SIZE); };
  const exportFiles = Array.isArray(document?.files) ? document.files : [];
  const exportDocuments = exportFiles.reduce((sum, f) => sum + (f?.connections?.length || 0), 0);

  const exportedAt = document?.exportedAt ? new Date(document.exportedAt) : null;
  // Connections that are kept are no longer part of what the import removes.
  const removeCount = summary?.remove ?? 0;
  const confirmLabel = writeCount > 0
    ? `Replace ${writeCount} connections in ${target.name}`
    : `Remove ${removeCount} connections in ${target.name}`;
  const nothingToChange = plan && writeCount === 0 && removeCount === 0;
  const busy = saving || checking;

  // Connections that cannot be found and connections that will be removed are the two ways an import loses settings.
  const needsAcknowledgement = missingCount > 0 || removeCount > 0;
  const connectionsText = (count) => `${count} ${count === 1 ? 'connection' : 'connections'}`;
  const acknowledgementText = [
    missingCount > 0 && `the ${connectionsText(missingCount)} that cannot be found`,
    removeCount > 0 && `the ${connectionsText(removeCount)} that will be removed`,
  ].filter(Boolean).join(' and ');

  return (
    <div data-state={saving ? 'saving' : step === 2 ? 'saved' : 'loaded'}>
      <TwPage>
        <nav aria-label="Breadcrumb" className="mb-4 text-sm text-gray-500 dark:text-gray-400">
          <button id="back-to-files" type="button" className="hover:underline disabled:opacity-50 disabled:no-underline" disabled={saving} onClick={backToFiles}>{target.name} logic files</button>
          <span className="mx-2" aria-hidden="true">/</span>
          <span aria-current="page">Import connections</span>
        </nav>
        <TwPage.Header>
          <TwPage.Header.Hdr.Fixed
            title={`Import connections into ${target.name}`}
            description="Replace the connections of the logic files in an export. Only the logic files in the export are changed, and nothing changes until you confirm on the review step." />
        </TwPage.Header>

        {!target.canImport && (
          <div role="alert" className="rounded-md bg-yellow-50 p-4 text-sm text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100">
            Connections can only be imported into the first environment of a local group, by someone with Templates configuration access.
          </div>
        )}

        {target.canImport && (
          <>
            <ol className="flex flex-wrap items-center gap-3 mb-6" aria-label="Progress">
              {steps.map((label, idx) => (
                <React.Fragment key={label}>
                  <li aria-current={idx === step ? 'step' : undefined}
                    className={classNames('flex items-center gap-2 text-sm font-medium',
                      idx === step ? 'text-indigo-600 dark:text-brand-blue' : idx < step ? 'text-gray-900 dark:text-gray-200' : 'text-gray-400')}>
                    <span className={classNames('flex h-6 w-6 items-center justify-center rounded-full border-2 text-xs',
                      idx < step ? 'border-indigo-600 bg-indigo-600 text-white dark:border-brand-blue dark:bg-brand-blue' : idx === step ? 'border-indigo-600 dark:border-brand-blue' : 'border-gray-300')}>
                      {idx < step ? <CheckIcon className="h-4 w-4" aria-hidden="true" /> : idx + 1}
                    </span>
                    {label}
                    {idx < steps.length - 1 && <span aria-hidden="true" className={classNames('ml-1 h-px w-10', idx < step ? 'bg-indigo-600 dark:bg-brand-blue' : 'bg-gray-300')} />}
                  </li>
                </React.Fragment>
              ))}
            </ol>
            <h3 ref={headingRef} tabIndex={-1} className="sr-only">{`Step ${step + 1} of ${steps.length}: ${steps[step]}`}</h3>

            {step === 0 && (
              <CmpPanel>
                <div aria-live="polite" className="max-w-xl">
                  <FileUpload label="Connections export" fileExtension=".json" maxFileSizeMb={maxExportFileMb} onChange={onFileChosen} error={fileError} />
                  {checking && <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Checking the file against {target.name}…</p>}
                </div>
              </CmpPanel>
            )}

            {step === 1 && plan && (
              <>
                <SourceStrip
                  source={`${plan.source?.customerEnvironment || 'an unknown environment'}${plan.source?.environmentGroupName ? ` in ${plan.source.environmentGroupName}` : ''}`}
                  exportedText={isValid(exportedAt) ? format(exportedAt, 'yyyy-MM-dd HH:mm') : null}
                  files={exportFiles.length}
                  documents={exportDocuments} />
                {plan.uncheckedReferences?.length > 0 && (
                  <p role="status" className="mb-4 rounded-md bg-yellow-50 p-3 text-sm text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100">
                    {plan.uncheckedReferences.join(' ')} Missing references may not be listed.
                  </p>
                )}
                <SummaryCards summary={summary} env={target.name} filter={filter} onToggle={toggleCard} />
                <UnconnectedPanel unconnected={summary.unconnected} env={target.name} removeUnconnected={removeUnconnected} onChange={setRemoveUnconnected} />
                {summary.cleanBill && <CleanBill env={target.name} />}

                <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-200">All changes by logic file</h4>
                  <div className="flex flex-wrap items-center gap-3">
                    <label htmlFor="review-search" className="sr-only">Search logic files and documents</label>
                    <input id="review-search" type="search" value={query} placeholder="Logic file or document" onChange={e => changeQuery(e.target.value)}
                      className="block w-full sm:w-72 rounded-md border border-gray-300 bg-white py-2 px-3 text-sm placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-brand-blue focus:border-brand-blue dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                    <CmpButton id="review-toggle-all" variant={CmpButton.variants.secondary} disabled={groups.length === 0} onClick={() => setAllOpen(!allOpen)}>
                      {allOpen ? 'Collapse all' : 'Expand all'}
                    </CmpButton>
                  </div>
                </div>

                {groups.length === 0 && (
                  <p className="py-6 text-sm text-gray-600 dark:text-gray-400">No logic file or document matches the search or the selected card.</p>
                )}
                {groups.slice(0, groupLimit).map(group => (
                  <LogicFileGroup key={group.fileName} group={group} env={target.name} open={isOpen(group)} resetKey={`${plan.planHash}|${filter}|${query}`}
                    onToggle={() => setOpenGroups(o => ({ ...o, [group.fileName]: !isOpen(group) }))} />
                ))}
                {groups.length > groupLimit && (
                  <CmpButton id="show-more-files" variant={CmpButton.variants.secondary} onClick={() => setGroupLimit(l => l + GROUP_PAGE_SIZE)}>
                    {`Show ${Math.min(GROUP_PAGE_SIZE, groups.length - groupLimit)} more logic files`}
                  </CmpButton>
                )}

                {plan.referenceWarnings.length > 0 && (
                  <details className="mt-4 rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
                    <summary className="cursor-pointer text-sm font-semibold text-gray-900 dark:text-gray-200">
                      {`Set up in ${target.name} before or after importing (${plan.referenceWarnings.length})`}
                    </summary>
                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">The connections are imported as they are. Fix these in the workflow or SmartForm tool afterwards.</p>
                    <div className="mt-2 overflow-x-auto"><ReferenceTable plan={plan} /></div>
                  </details>
                )}

                {checking && (
                  <p role="status" className="mt-6 text-sm text-gray-600 dark:text-gray-400">Checking the file against {target.name} again…</p>
                )}
                {(planChanged || applyError) && !checking && (
                  <div role="alert" className="mt-6 rounded-md bg-red-50 p-4 text-sm text-red-800 dark:bg-red-900 dark:text-red-100">
                    {planChanged
                      ? <>The connections in {target.name} changed after the check, so nothing was imported. <button id="check-again" type="button" className="font-semibold underline" onClick={() => check(document)}>Check again</button></>
                      : applyError}
                  </div>
                )}

                <div className="mt-6 py-3 border-t border-gray-200 dark:border-gray-700 flex flex-wrap items-center gap-3">
                  {needsAcknowledgement && (
                    <label htmlFor="ack-not-found" className="flex items-center gap-2 text-sm text-gray-900 dark:text-gray-200">
                      <input id="ack-not-found" type="checkbox" checked={acknowledged} onChange={e => setAcknowledged(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600" />
                      I have reviewed {acknowledgementText}
                    </label>
                  )}
                  <div className="flex-1" />
                  <span className="text-xs text-gray-500 dark:text-gray-400">A backup download starts first. Keep the file until you have checked the result.</span>
                  <CmpButton id="import-back" variant={CmpButton.variants.secondary} disabled={busy} onClick={() => setStep(0)}>Back</CmpButton>
                  <CmpButton id="import-confirm" variant={CmpButton.variants.primary}
                    disabled={busy || planChanged || nothingToChange || (needsAcknowledgement && !acknowledged)} onClick={apply}>
                    {saving ? 'Replacing…' : nothingToChange ? 'Nothing to change' : confirmLabel}
                  </CmpButton>
                </div>
              </>
            )}

            {step === 2 && result && (
              <CmpPanel>
                <div id="import-result" className="space-y-2">
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-200">
                    {result.error ? `The import stopped partway in ${target.name}` : `Connections replaced in ${target.name}`}
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {result.createdCount} created, {result.updatedCount} updated, {result.removedCount} removed{result.keptCount > 0 ? `, ${result.keptCount} kept` : ''}. {result.notFoundCount} could not be found and were skipped.
                  </p>
                  {result.error
                    ? <p className="text-sm text-red-700 dark:text-red-300">{result.error} The backup downloaded before the import holds the connections from before.</p>
                    : <p className="text-sm text-gray-600 dark:text-gray-400">Publish to the next environment as usual to carry them on.</p>}
                  <CmpButton id="import-done" variant={CmpButton.variants.primary} onClick={backToFiles}>Back to logic files</CmpButton>
                </div>
              </CmpPanel>
            )}
          </>
        )}
      </TwPage>
    </div>
  );
}
